// hooks/useAuth.js
import React, { createContext, useState, useContext, useEffect } from 'react';
import PropTypes from 'prop-types';
import CustomSpinner from 'components/common/CustomSpinner';
import axios from 'axios';
import { additionalRoutes } from 'routes/routeConfig';
import { getTenantFromToken } from 'utils/routeUtils';
import { authenticationApi } from 'api/endpoints/Authentication';
import { jwtDecode } from 'jwt-decode';

// Create auth context
const AuthContext = createContext(null);

// API endpoints
const API_BASE_URL = process.env.REACT_APP_API_URL;
const LOGIN_ENDPOINT = `${API_BASE_URL}${authenticationApi.login()}`;
const REFRESH_ENDPOINT = `${API_BASE_URL}${authenticationApi.refresh()}`;
const LOGOUT_ENDPOINT = `${API_BASE_URL}${authenticationApi.logout()}`;

// Flag to prevent multiple simultaneous refresh attempts
let isRefreshing = false;
// Store pending requests that should be retried after token refresh
let pendingRequests = [];

// Define public routes that don't need authentication
const PUBLIC_ROUTES = ['/login', '/public', '/offline-data'];

// Setup axios instance with interceptors for token handling
const api = axios.create({
  baseURL: API_BASE_URL
});

export const AuthProvider = ({ children }) => {
  // State variables
  const [tenantId, setTenantId] = useState(null); // Changed to state variable
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [authTokens, setAuthTokens] = useState(() => {
    try {
      const savedTokens = localStorage.getItem('auth_tokens');
      return savedTokens ? JSON.parse(savedTokens) : null;
    } catch (e) {
      console.error('Error reading tokens from localStorage', e);
      return null;
    }
  });

  // Update tenant ID whenever auth tokens change
  useEffect(() => {
    if (authTokens?.access_token) {
      const decodedTenant = getTenantFromToken();
      setTenantId(decodedTenant);
    } else {
      setTenantId(null);
    }
  }, [authTokens]);

  // Helper function to check if current route is public
  const isPublicRoute = () => {
    const pathname = window.location.pathname;
    return PUBLIC_ROUTES.some(route => pathname.includes(route));
  };

  // Setup axios interceptor for adding auth token to requests
  useEffect(() => {
    // Request interceptor
    const requestInterceptor = api.interceptors.request.use(
      config => {
        // Skip auth for public routes or if we're offline
        const currentPathname = window.location.pathname;
        const isCurrentRoutePublic = PUBLIC_ROUTES.some(route =>
          currentPathname.includes(route)
        );

        // Only add auth token if we have one and the route isn't public
        if (authTokens?.access_token && !isCurrentRoutePublic) {
          config.headers.Authorization = `Bearer ${
            JSON.parse(localStorage.getItem('auth_tokens'))?.access_token
          }`;
        }
        return config;
      },
      error => Promise.reject(error)
    );

    // Response interceptor to handle token refresh
    const responseInterceptor = api.interceptors.response.use(
      response => response,
      async error => {
        const originalRequest = error.config;

        // Skip token refresh logic for public routes
        const currentPathname = window.location.pathname;
        const isCurrentRoutePublic = PUBLIC_ROUTES.some(route =>
          currentPathname.includes(route)
        );

        if (isCurrentRoutePublic) {
          return Promise.reject(error);
        }

        // If error is 401 and we haven't already tried to refresh and we have a refresh token
        if (
          error.response?.status === 401 &&
          !originalRequest._retry &&
          authTokens?.refresh_token &&
          !isOffline
        ) {
          originalRequest._retry = true;

          // If already refreshing, wait for that to complete
          if (isRefreshing) {
            try {
              return new Promise((resolve, reject) => {
                pendingRequests.push({ resolve, reject, originalRequest });
              });
            } catch (e) {
              return Promise.reject(e);
            }
          }

          isRefreshing = true;

          try {
            console.log('Refreshing token due to 401 error');
            const response = await axios.post(REFRESH_ENDPOINT, {
              refresh_token: authTokens.refresh_token
            });

            const newTokens = response.data;
            setAuthTokens(newTokens);
            localStorage.setItem('auth_tokens', JSON.stringify(newTokens));

            const expiryTime = new Date();
            expiryTime.setSeconds(
              expiryTime.getSeconds() + newTokens.expires_in
            );
            localStorage.setItem('token_expiry', expiryTime.toISOString());

            originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;

            pendingRequests.forEach(request => {
              request.originalRequest.headers.Authorization = `Bearer ${newTokens.access_token}`;
              request.resolve(axios(request.originalRequest));
            });
            pendingRequests = [];

            isRefreshing = false;
            return axios(originalRequest);
          } catch (refreshError) {
            console.error('Token refresh failed:', refreshError);

            pendingRequests.forEach(request => {
              request.reject(refreshError);
            });
            pendingRequests = [];

            isRefreshing = false;

            // Force logout and redirect to login
            logout();

            // Prevent automatic redirect by navigating to login with state
            window.location.href = '/login';

            return Promise.reject(refreshError);
          }
        }

        // For other 401 errors (like when refresh token is invalid), logout immediately
        if (error.response?.status === 401) {
          logout();
          window.location.href = '/login';
        }

        return Promise.reject(error);
      }
    );

    // Clean up interceptors on unmount
    return () => {
      api.interceptors.request.eject(requestInterceptor);
      api.interceptors.response.eject(responseInterceptor);
    };
  }, [authTokens, isOffline]);

  // Track online/offline status
  useEffect(() => {
    const handleOnline = () => setIsOffline(false);
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check token expiration - less frequent checks to prevent excessive refreshes
  useEffect(() => {
    // Skip token checks on public routes
    if (isPublicRoute()) {
      return;
    }

    if (authTokens && authTokens.expires_in) {
      // Calculate when we should start checking - when token is at 75% of its lifetime
      const expiresInMs = authTokens.expires_in * 1000;
      const refreshThreshold = expiresInMs * 0.75; // Wait until 75% of token lifetime has passed

      // Set a timeout to check once when we reach the threshold, instead of checking repeatedly
      const initialTimeout = setTimeout(() => {
        if (!isOffline) {
          // Once we reach threshold, set up periodic checks but less frequently
          const checkTokenExpiry = setInterval(() => {
            const tokenExpiryStr = localStorage.getItem('token_expiry');
            // Skip if we don't have a valid expiry time
            if (!tokenExpiryStr) return;

            try {
              const tokenExpiryTime = new Date(tokenExpiryStr);
              const now = new Date();

              // Validate date is valid
              if (isNaN(tokenExpiryTime.getTime())) {
                console.warn('Invalid token expiry time found in localStorage');
                return;
              }

              // If token will expire in less than 2 minutes and we're online, refresh it
              if (
                tokenExpiryTime.getTime() - now.getTime() < 120000 &&
                !isOffline
              ) {
                console.log('Token nearing expiration, refreshing...');
                refreshToken().catch(err => {
                  console.error(
                    'Failed to refresh token during scheduled check:',
                    err
                  );
                });

                // Clear this interval after triggering a refresh
                clearInterval(checkTokenExpiry);
              }

              // If token is expired and we're offline, keep the user logged in for offline access
              if (tokenExpiryTime < now && isOffline) {
                console.log(
                  'Token expired but in offline mode - maintaining session for offline access'
                );
              }
            } catch (e) {
              console.error('Error checking token expiry:', e);
            }
          }, 60000); // Check every minute instead of every 30 seconds

          return () => clearInterval(checkTokenExpiry);
        }
      }, Math.max(1000, refreshThreshold)); // At least 1 second delay

      return () => clearTimeout(initialTimeout);
    }
  }, [authTokens, isOffline]);

  // Initial auth check - simplified to avoid excessive API calls
  useEffect(() => {
    let isMounted = true;

    const setupAuth = async () => {
      // Skip auth check on public routes
      if (isPublicRoute()) {
        setIsLoading(false);
        return;
      }

      // Safety timeout to prevent infinite loading
      const safetyTimeout = setTimeout(() => {
        if (isLoading && isMounted) {
          setIsLoading(false);

          // If we have tokens, consider the user authenticated (even if token is expired in offline mode)
          if (authTokens) {
            setIsAuthenticated(true);
          }
        }
      }, 3000); // 3 second timeout

      if (authTokens) {
        // Instead of making an API call to validate, check if token is expired
        const tokenExpiryStr = localStorage.getItem('token_expiry');
        let tokenExpiry = null;

        if (tokenExpiryStr && tokenExpiryStr !== 'undefined') {
          try {
            tokenExpiry = new Date(tokenExpiryStr);
            if (isNaN(tokenExpiry.getTime())) {
              tokenExpiry = null;
            }
          } catch (e) {
            console.error('Error parsing token expiry:', e);
            tokenExpiry = null;
          }
        }

        const now = new Date();

        // If we're online and token is expired, try to refresh it
        if (
          !isOffline &&
          tokenExpiry &&
          tokenExpiry <= now &&
          authTokens.refresh_token
        ) {
          try {
            await refreshToken();
            if (isMounted) {
              setIsAuthenticated(true);
            }
          } catch (refreshError) {
            console.error(
              'Failed to refresh token during initial auth check:',
              refreshError
            );
            // If refresh fails, clear auth state
            if (isMounted) {
              setAuthTokens(null);
              setIsAuthenticated(false);
              localStorage.removeItem('auth_tokens');
              localStorage.removeItem('user');
              localStorage.removeItem('token_expiry');
            }
          }
        } else {
          // Token is still valid or we're offline, consider authenticated
          setIsAuthenticated(true);
        }
      }

      if (isMounted) {
        setIsLoading(false);
        clearTimeout(safetyTimeout);
      }
    };

    setupAuth();

    return () => {
      isMounted = false;
    };
  }, [authTokens, isOffline]); // isLoading removed from deps — effect sets isLoading, including it caused an infinite re-render loop (blinking screen)

  // Improved getDefaultRoute to handle cluster paths
  // Improved getDefaultRoute function with better error handling
  const getDefaultRoute = () => {
    try {
      // First try to get tenant ID from state, and if not available, get from token
      let currentTenantId = tenantId;

      if (!currentTenantId) {
        const savedTokens = localStorage.getItem('auth_tokens');

        if (savedTokens) {
          try {
            const parsedTokens = JSON.parse(savedTokens);
            if (parsedTokens?.access_token) {
              const decodedToken = jwtDecode(parsedTokens.access_token);
              currentTenantId = decodedToken?.tenant_id;
            }
          } catch (e) {
            console.error('Error parsing tokens or decoding JWT:', e);
          }
        }
      }

      // Check if tenant ID is available
      if (!currentTenantId) {
        console.log('No tenant ID available, returning login route');
        return '/login'; // Redirect to login if no tenant ID
      }

      // Get navigation items from localStorage with error handling
      let navItems = [];
      try {
        const navItemsStr = localStorage.getItem('nav_items');
        if (navItemsStr && navItemsStr !== 'undefined') {
          navItems = JSON.parse(navItemsStr);
          // Ensure it's an array
          if (!Array.isArray(navItems)) {
            navItems = [];
          }
        }
      } catch (e) {
        console.error('Error parsing nav_items:', e);
        navItems = [];
      }

      if (navItems.length === 0) {
        console.log(
          'No navigation items found, returning default tenant route'
        );
        return `/${currentTenantId}/dashboard`; // Fallback to dashboard
      }

      // Additional routes from localStorage with error handling
      let routeConfig = additionalRoutes; // Use imported routes as default
      try {
        const additionalRoutesStr = localStorage.getItem('additional_routes');
        if (additionalRoutesStr && additionalRoutesStr !== 'undefined') {
          const localAdditionalRoutes = JSON.parse(additionalRoutesStr);
          if (
            localAdditionalRoutes &&
            typeof localAdditionalRoutes === 'object'
          ) {
            routeConfig = localAdditionalRoutes;
          }
        }
      } catch (e) {
        console.error('Error parsing additional_routes:', e);
      }

      let routePath = '';

      // Check if we have structured nav items
      if (navItems.length > 0) {
        const firstItem = navItems[0];

        // If the first item has children, use the first child
        if (
          firstItem.children &&
          Array.isArray(firstItem.children) &&
          firstItem.children.length > 0
        ) {
          const childName = firstItem.children[0].name;

          // Look up the path in routeConfig using the name as key
          if (routeConfig[childName] && routeConfig[childName].path) {
            routePath = routeConfig[childName].path;
          } else {
            // If not found in routeConfig, use name directly as fallback
            routePath = childName;
          }
        } else {
          // No children, check if item name exists in routeConfig
          if (
            firstItem.name &&
            routeConfig[firstItem.name] &&
            routeConfig[firstItem.name].path
          ) {
            routePath = routeConfig[firstItem.name].path;
          } else if (routeConfig[firstItem] && routeConfig[firstItem].path) {
            // Try using the item itself as a key
            routePath = routeConfig[firstItem].path;
          } else {
            // Last resort - use the item itself or its name
            routePath = firstItem.name || firstItem || 'dashboard';
          }
        }
      }

      // If we found a path, return it with the tenant ID
      if (routePath) {
        // Check if routePath already starts with a slash
        if (routePath.startsWith('/')) {
          routePath = routePath.substring(1); // Remove leading slash
        }

        // Special handling for monitor and plotoverlay paths
        if (routePath === 'monitor' || routePath === 'plotoverlay') {
          // Try to get the first monitoring cluster from localStorage
          let clusterToUse = '';

          try {
            const firstClusterStr = localStorage.getItem(
              'first_monitoring_cluster'
            );
            if (firstClusterStr && firstClusterStr !== 'undefined') {
              clusterToUse = firstClusterStr;
            } else {
              const monitoringDataStr = localStorage.getItem(
                'monitoring_location_data'
              );
              if (monitoringDataStr && monitoringDataStr !== 'undefined') {
                const monitoringData = JSON.parse(monitoringDataStr);
                if (
                  Array.isArray(monitoringData) &&
                  monitoringData.length > 0
                ) {
                  clusterToUse = monitoringData[0].key;
                }
              }
            }
          } catch (e) {
            console.error('Error parsing monitoring data:', e);
          }

          // If we found a cluster, append it to the path
          if (clusterToUse) {
            return `/${currentTenantId}/${routePath}/${clusterToUse}`;
          }

          // Otherwise, just return the path without a cluster
          return `/${currentTenantId}/${routePath}`;
        }

        // For other paths, just return the normal path
        const fullPath = `/${currentTenantId}/${routePath}`;
        // console.log('Returning default route:', fullPath);
        return fullPath;
      }

      // Return default path with tenant ID
      console.log('No specific route found, returning tenant dashboard');
      return `/${currentTenantId}/dashboard`;
    } catch (error) {
      console.error('Error determining default path:', error);
      // Fallback to a safe default path
      return tenantId ? `/${tenantId}/dashboard` : '/login';
    }
  };

  // Token refresh function with debouncing to prevent multiple calls
  const refreshToken = async () => {
    if (!authTokens?.refresh_token) {
      throw new Error('No refresh token available');
    }

    // Skip token refresh on public routes
    if (isPublicRoute()) {
      return Promise.resolve(null);
    }

    // If already refreshing, don't make another call
    if (isRefreshing) {
      return new Promise((resolve, reject) => {
        // Add to pending requests to be resolved when the current refresh completes
        pendingRequests.push({
          resolve,
          reject,
          originalRequest: null
        });
      });
    }

    isRefreshing = true;
    console.log('Starting token refresh');

    try {
      const response = await axios.post(REFRESH_ENDPOINT, {
        refresh_token: authTokens.refresh_token
      });

      const newTokens = response.data;
      setAuthTokens(newTokens);

      // Store tokens and set expiry time
      localStorage.setItem('auth_tokens', JSON.stringify(newTokens));
      const expiryTime = new Date();
      expiryTime.setSeconds(expiryTime.getSeconds() + newTokens.expires_in);
      localStorage.setItem('token_expiry', expiryTime.toISOString());

      // Resolve all pending requests
      pendingRequests.forEach(request => {
        request.resolve(newTokens);
      });
      pendingRequests = [];

      isRefreshing = false;
      console.log('Token refresh completed successfully');
      return newTokens;
    } catch (error) {
      console.error('Failed to refresh token:', error);

      // Reject all pending requests
      pendingRequests.forEach(request => {
        request.reject(error);
      });
      pendingRequests = [];

      isRefreshing = false;
      throw error;
    }
  };

  // Login function with Keycloak integration
  const login = async (username, password) => {
    try {
      const response = await axios.post(LOGIN_ENDPOINT, {
        username,
        password
      });

      const tokens = response.data;
      setAuthTokens(tokens);

      // Store navigation items and route configuration from response
      if (response.data.nav_items) {
        localStorage.setItem(
          'nav_items',
          JSON.stringify(response.data.nav_items)
        );
      }

      if (response.data.nav_bar_items) {
        localStorage.setItem(
          'nav_bar_items',
          JSON.stringify(response.data.nav_bar_items)
        );
      }

      if (response.data.additional_routes) {
        localStorage.setItem(
          'additional_routes',
          JSON.stringify(response.data.additional_routes)
        );
      }

      // Store tokens and set expiry time
      localStorage.setItem('auth_tokens', JSON.stringify(tokens));
      const expiryTime = new Date();
      expiryTime.setSeconds(expiryTime.getSeconds() + tokens.expires_in);
      localStorage.setItem('token_expiry', expiryTime.toISOString());

      // The tenant ID will be updated automatically through the useEffect hook
      // when authTokens are updated
      setIsAuthenticated(true);

      return;
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  const logout = async () => {
    try {
      if (authTokens?.refresh_token && !isOffline) {
        // Call logout endpoint if we're online
        await axios.post(LOGOUT_ENDPOINT, {
          refresh_token: authTokens.refresh_token
        });
      }
    } catch (error) {
      console.error('Error during logout:', error);
    } finally {
      // Clear auth state regardless of API call success
      setAuthTokens(null);
      setTenantId(null); // Clear tenant ID
      setIsAuthenticated(false);

      // Clear localStorage
      localStorage.removeItem('auth_tokens');
      localStorage.removeItem('user');
      localStorage.removeItem('token_expiry');
      localStorage.removeItem('nav_items');
      localStorage.removeItem('nav_bar_items');
      localStorage.removeItem('additional_routes');
      localStorage.removeItem('tenant_id');
      localStorage.removeItem('tenant');
      localStorage.removeItem('first_monitoring_cluster');
      localStorage.removeItem('monitoring_location_data');
    }
  };

  // Loading state
  if (isLoading) {
    return <CustomSpinner />;
  }

  // Context value
  const value = {
    isAuthenticated,
    isOffline,
    tenantId, // Expose tenant ID in context
    login,
    logout,
    refreshToken,
    getDefaultRoute,
    api // Expose the configured axios instance
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

AuthProvider.propTypes = {
  children: PropTypes.node
};

export const useAuth = () => {
  return useContext(AuthContext);
};

export default api; // Export the configured axios instance for use elsewhere
